// Locale controls display; calendar inputs always preserve the product clock.

import { zonedTime } from "@zooi/domain";

import { locale } from "./i18n.ts";

export const LOCALE = locale();

/** datetime-local fields describe the product's clock, independent of the device's clock. */
export function dateTimeInput(iso: string | null, timeZone: string) {
  if (!iso) return "";
  const parts = Object.fromEntries(new Intl.DateTimeFormat(LOCALE, { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso)).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}
export function inputDateTime(value: string, timeZone: string): string | null {
  if (!value) return null;
  const [date, time] = value.split("T"), [hours, minutes] = time.split(":").map(Number);
  return new Date(zonedTime(date, hours, minutes, timeZone)).toISOString();
}

const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });

/** "in 3 hours", "2 days ago". */
export function relative(iso: string | null | undefined, now = Date.now()) {
  if (!iso) return "";
  const diff = Date.parse(iso) - now;
  const abs = Math.abs(diff);
  const minutes = Math.round(diff / 60_000);
  if (abs < 60 * 60_000) return rtf.format(minutes, "minute");
  const hours = Math.round(diff / 3_600_000);
  if (abs < 48 * 3_600_000) return rtf.format(hours, "hour");
  return rtf.format(Math.round(diff / 86_400_000), "day");
}

/** "Oct 1, 9:30 AM" in the product's time zone. */
export function dateTime(iso: string | null | undefined, timeZone?: string) {
  if (!iso) return "";
  return new Intl.DateTimeFormat(LOCALE, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone }).format(new Date(iso));
}

/**
 * A deadline as the sidebar's Upcoming shows it (docs/design-system.md §0.2):
 * the time today, the weekday within the week, else the date; a passed one as
 * "2 hours ago". Days are the viewer's own, like Notion's upcoming events.
 */
export function shortDue(iso: string, now = Date.now(), timeZone?: string) {
  const at = Date.parse(iso);
  if (at <= now) return relative(iso, now);
  const today = localToday(timeZone ?? browserTimeZone(), now), then = localToday(timeZone ?? browserTimeZone(), at);
  const days = Math.round((Date.parse(`${then}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  if (days === 0) return new Intl.DateTimeFormat(LOCALE, { hour: "numeric", minute: "2-digit", timeZone }).format(at);
  if (days < 7) return new Intl.DateTimeFormat(LOCALE, { weekday: "short", timeZone }).format(at);
  return new Intl.DateTimeFormat(LOCALE, { month: "short", day: "numeric", timeZone }).format(at);
}

/** "Oct 1" for a local calendar date. */
export function day(date: string | null | undefined) {
  if (!date) return "";
  return new Intl.DateTimeFormat(LOCALE, { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${date}T00:00:00Z`));
}

/** Items joined the way the person's language joins them ("a, b and c", "a、b和c"). */
export const list = (items: string[]) => new Intl.ListFormat(LOCALE, { style: "long", type: "conjunction" }).format(items);
export const number = (n: number | null | undefined) => (n == null ? "–" : new Intl.NumberFormat(LOCALE).format(n));

export const decimal = (n: number | null | undefined, signed = false) => n == null ? "–" : new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2, signDisplay: signed ? "exceptZero" : "auto" }).format(n);

/** Ratios are stored as fractions; a missing denominator remains unknown. */
export const percent = (ratio: number | null | undefined, signed = false) => ratio == null ? "–" : new Intl.NumberFormat(LOCALE, { style: "percent", maximumFractionDigits: 2, signDisplay: signed ? "exceptZero" : "auto" }).format(ratio);

export const usd = (n: number | null | undefined) => n == null ? "–" : new Intl.NumberFormat(LOCALE, { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);
export const metricValue = (value: number | null | undefined, kind: "count" | "money" | "rate") => kind === "rate" ? percent(value) : decimal(value);

export function minutes(value: number) {
  if (LOCALE === "en-US") return value >= 60 ? `${Math.floor(value / 60)} h ${value % 60 ? `${value % 60} min` : ""}`.trim() : `${value} min`;
  const unit = (amount: number, name: "hour" | "minute") => new Intl.NumberFormat(LOCALE, { style: "unit", unit: name, unitDisplay: "long" }).format(amount);
  if (value < 60) return unit(value, "minute");
  return [unit(Math.floor(value / 60), "hour"), value % 60 ? unit(value % 60, "minute") : ""].filter(Boolean).join(" ");
}

/** Time zones the browser knows, for the settings picker. */
export const timeZones = (): string[] => {
  const list = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone");
  return list?.length ? ["UTC", ...list.filter((z) => z !== "UTC")] : ["UTC"];
};

export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/** Today's calendar date (YYYY-MM-DD) in a time zone, as a date input expects it. */
export function localToday(timeZone: string, now = Date.now()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat(LOCALE, { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** An hour of the product's local day, without applying the device's time zone. */
/** The name of an ISO weekday (Monday 1 … Sunday 7). */
export const weekdayName = (value: number) => new Intl.DateTimeFormat(LOCALE, { weekday: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 0, 4 + value)));
export const hour = (value: number) => new Intl.DateTimeFormat(LOCALE, { hour: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 0, 1, value)));

/**
 * A recorded amount in its own currency, with the currency's own decimals ("$12.50", "¥300"); a recorded fraction a
 * currency does not use is still shown ("¥300.50"). Never converted.
 */
export const money = (amount: string | number | null | undefined, currency: string) => {
  if (amount == null || amount === "") return "–";
  const value = typeof amount === "number" ? amount : Number(amount);
  try {
    const own = new Intl.NumberFormat(LOCALE, { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
    const scaled = value * 10 ** own;
    const digits = Math.abs(Math.round(scaled) - scaled) < 1e-6 ? own : 2;
    return new Intl.NumberFormat(LOCALE, { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
  } catch { return `${decimal(value)} ${currency}`; }
};
/** "Oct 2026" for a YYYY-MM month. */
export const monthLabel = (month: string) => new Intl.DateTimeFormat(LOCALE, { month: "short", year: "2-digit", timeZone: "UTC" }).format(new Date(`${month}-01T00:00:00Z`));
/** "Oct 1, 2026" for a local calendar date. */
export const fullDay = (date: string) => new Intl.DateTimeFormat(LOCALE, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${date}T00:00:00Z`));
